/**
 * `bite` — wolf, boar, dog, and whatever else in Annwn decides a person is
 * meat.
 *
 * Mechanically a puncture: teeth and tusks make the same kind of narrow,
 * deep, dirty hole a spear does, and authoring fifty-four near-identical
 * entries for the privilege of watching them drift apart over three balance
 * passes would be a waste of everyone's time (AoD open question 4). So `bite`
 * is an **overlay**: puncture's numbers, a markedly worse infection modifier,
 * and its own prose.
 *
 * The infection modifier is the whole point. A spear wound and a wolf bite of
 * the same level look alike on the day and diverge over the fortnight that
 * follows — which is the mechanic M1-05 exists to run, and the reason a
 * survivable mauling is still a way to die.
 */
const NARRATION = {
    head: [
        [
            '{attacker} snaps at {victim}’s face and the teeth scrape.',
            '{attacker}’s jaws close on air beside {victim}’s head.',
        ],
        [
            '{attacker}’s teeth tear {victim}’s scalp.',
            '{attacker} lays open {victim}’s cheek with a snap.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s face and tear away skin.',
            '{attacker} bites deep into {victim}’s scalp and shakes.',
        ],
        [
            '{attacker}’s teeth grate on the bone of {victim}’s skull.',
            '{attacker} takes {victim}’s ear and part of the scalp with it.',
        ],
        [
            '{attacker}’s jaws crush the bone of {victim}’s face.',
            '{attacker} tears {victim}’s jaw half away.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s skull and it gives.',
            '{attacker} tears {victim}’s face away.',
        ],
    ],
    eye: [
        [
            '{attacker}’s teeth pass close by {victim}’s {part}.',
            '{attacker} snaps and the whiskers rake {victim}’s {part}.',
        ],
        [
            '{attacker}’s teeth tear the lid of {victim}’s {part}.',
            '{attacker} opens the flesh beside {victim}’s {part}.',
        ],
        [
            '{attacker}’s teeth take {victim}’s {part}.',
            '{attacker} bites across {victim}’s face and the {part} is lost.',
        ],
        [
            '{attacker} tears {victim}’s {part} from its socket.',
            '{attacker}’s teeth burst {victim}’s {part}.',
        ],
        [
            '{attacker}’s jaws empty the socket of {victim}’s {part}.',
            '{attacker} tears away {victim}’s {part} and the bone around it.',
        ],
        [
            '{attacker}’s teeth drive through {victim}’s {part} and into the skull.',
            '{attacker} takes {victim}’s {part} and does not stop there.',
        ],
    ],
    neck: [
        [
            '{attacker}’s teeth scrape {victim}’s throat.',
            '{attacker} snaps at {victim}’s neck and misses the vein.',
        ],
        [
            '{attacker}’s teeth tear the skin of {victim}’s neck.',
            '{attacker} nips {victim}’s throat and blood wells up.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s neck and tear.',
            '{attacker} bites into the muscle of {victim}’s throat.',
        ],
        [
            '{attacker}’s teeth find the vessels of {victim}’s neck.',
            '{attacker} bites deep into {victim}’s throat and shakes.',
        ],
        ['{attacker} tears {victim}’s throat open.', '{attacker}’s jaws crush {victim}’s windpipe.'],
        [
            '{attacker} tears out {victim}’s throat.',
            '{attacker}’s jaws close on {victim}’s neck and the spine parts.',
        ],
    ],
    chest: [
        [
            '{attacker}’s teeth scrape across {victim}’s ribs.',
            '{attacker} snaps at {victim}’s chest and catches cloth.',
        ],
        [
            '{attacker}’s teeth tear the skin of {victim}’s chest.',
            '{attacker} nips {victim} across the ribs.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s chest and tear away flesh.',
            '{attacker} bites into the muscle over {victim}’s ribs.',
        ],
        [
            '{attacker}’s teeth grate between {victim}’s ribs.',
            '{attacker} bites deep into {victim}’s chest and worries at it.',
        ],
        [
            '{attacker}’s jaws break {victim}’s ribs and reach the lung.',
            '{attacker} tears {victim}’s chest open and the breath bubbles.',
        ],
        ['{attacker} tears open {victim}’s chest.', '{attacker}’s jaws close on {victim}’s heart.'],
    ],
    abdomen: [
        [
            '{attacker}’s teeth scrape {victim}’s flank.',
            '{attacker} snaps at {victim}’s belly and catches cloth.',
        ],
        [
            '{attacker}’s teeth tear the skin of {victim}’s belly.',
            '{attacker} nips {victim} across the hip.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s belly and tear.',
            '{attacker} bites into the muscle of {victim}’s flank.',
        ],
        [
            '{attacker}’s teeth open {victim}’s belly to the gut.',
            '{attacker} bites deep into {victim}’s side and drags.',
        ],
        [
            '{attacker} tears {victim}’s belly open and the gut comes with it.',
            '{attacker}’s jaws take {victim}’s side away in a mouthful.',
        ],
        [
            '{attacker} guts {victim} with a single wrench of its head.',
            '{attacker} tears {victim}’s belly open to the spine.',
        ],
    ],
    arm: [
        [
            '{attacker}’s teeth scrape {victim}’s {part}.',
            '{attacker} snaps at {victim}’s {part} and catches sleeve.',
        ],
        [
            '{attacker}’s teeth tear the skin of {victim}’s {part}.',
            '{attacker} nips {victim}’s {part}.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s {part} and tear.',
            '{attacker} bites into the muscle of {victim}’s {part} and holds.',
        ],
        [
            '{attacker}’s teeth grate on the bone of {victim}’s {part}.',
            '{attacker} bites deep into {victim}’s {part} and worries at it.',
        ],
        [
            '{attacker}’s jaws break the bone of {victim}’s {part}.',
            '{attacker} strips the muscle from {victim}’s {part}.',
        ],
        [
            '{attacker} tears {victim}’s {part} away.',
            '{attacker}’s jaws take {victim}’s {part} off at the shoulder.',
        ],
    ],
    hand: [
        [
            '{attacker}’s teeth scrape {victim}’s {part}.',
            '{attacker} snaps at {victim}’s {part} and misses.',
        ],
        [
            '{attacker}’s teeth tear the back of {victim}’s {part}.',
            '{attacker} nips {victim}’s fingers.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s {part} and the grip fails.',
            '{attacker} bites through {victim}’s palm.',
        ],
        [
            '{attacker}’s teeth take two fingers from {victim}’s {part}.',
            '{attacker} bites through the tendons of {victim}’s {part}.',
        ],
        [
            '{attacker}’s jaws crush the bones of {victim}’s {part}.',
            '{attacker} tears {victim}’s {part} into a ruin.',
        ],
        [
            '{attacker} takes {victim}’s {part} off at the wrist.',
            '{attacker}’s jaws tear {victim}’s {part} away.',
        ],
    ],
    leg: [
        [
            '{attacker}’s teeth scrape {victim}’s {part}.',
            '{attacker} snaps at {victim}’s {part} and catches cloth.',
        ],
        [
            '{attacker}’s teeth tear the skin of {victim}’s {part}.',
            '{attacker} nips {victim}’s {part}.',
        ],
        [
            '{attacker}’s jaws close on {victim}’s {part} and {victim} staggers.',
            '{attacker} bites into the muscle of {victim}’s {part} and holds.',
        ],
        [
            '{attacker}’s teeth grate on the bone of {victim}’s {part}.',
            '{attacker} bites deep into {victim}’s {part} and drags {them} down.',
        ],
        [
            '{attacker}’s jaws break the bone of {victim}’s {part}.',
            '{attacker} strips the muscle from {victim}’s {part}.',
        ],
        [
            '{attacker} tears {victim}’s {part} away.',
            '{attacker}’s jaws take {victim}’s {part} off at the thigh.',
        ],
    ],
    foot: [
        [
            '{attacker}’s teeth scrape {victim}’s {part}.',
            '{attacker} snaps at {victim}’s {part} and misses.',
        ],
        ['{attacker}’s teeth tear the skin of {victim}’s {part}.', '{attacker} nips {victim}’s heel.'],
        [
            '{attacker}’s jaws close on {victim}’s {part} and {victim} limps on it.',
            '{attacker} bites through the top of {victim}’s {part}.',
        ],
        [
            '{attacker}’s teeth part the tendon at {victim}’s heel.',
            '{attacker} bites through {victim}’s ankle and holds.',
        ],
        [
            '{attacker}’s jaws crush the bones of {victim}’s {part}.',
            '{attacker} tears {victim}’s {part} into a ruin.',
        ],
        [
            '{attacker} takes {victim}’s {part} off at the ankle.',
            '{attacker}’s jaws tear {victim}’s {part} away.',
        ],
    ],
};
export const BITE_OVERLAY = {
    type: 'bite',
    from: 'puncture',
    /**
     * The number that makes a bite a bite. A mouth is filthier than a spear
     * point by a margin the era had no answer to; M1-05 turns this into the
     * fortnight after the fight.
     */
    infectionMod: 0.15,
    narration: NARRATION,
};
//# sourceMappingURL=bite.js.map